/// Zustand store mirroring the small slice of session state the React
/// chrome cares about. The Pixi loop reads/writes the GameSession
/// directly; this store exists so React components can subscribe to
/// selection + level-end without going through Pixi.

import { create } from 'zustand'
import type {
  LevelEndState,
  ScoreBreakdown,
  ScoreEvent,
} from '../game/GameSession'
import type { AircraftStatus, EmergencyReason } from '../game/Aircraft'
import type { AircraftType } from '../types/LevelData'

export interface AircraftSummary {
  id: string
  flightNumber: string
  headingDeg: number
  targetHeadingDeg: number
  altitudeFt: number
  targetAltitudeFt: number
  speedKts: number
  targetSpeedKts: number
  /// Min/max speed for this aircraft type (clamps the Speed field).
  minSpeedKts: number
  maxSpeedKts: number
  /// Aircraft type drives type-specific UI affordances — e.g.
  /// helicopters get the Hover button + relaxed clearance gates.
  type: AircraftType
  status: AircraftStatus
  /// Whether the cleared-to-land button should be enabled for this
  /// aircraft right now. Recomputed each tick by RadarScene.
  canClearToLand: boolean
  /// When canClearToLand is false, the *first* gate that rejected
  /// the clearance — drives the disabled-button label so the player
  /// can see what they need to fix (alignment, distance, descent
  /// rate, centerline). Null when ok or when the aircraft is already
  /// cleared/landed.
  clearToLandRejection:
    | 'not_aligned'
    | 'off_centerline'
    | 'too_far'
    | 'descent_too_steep'
    | 'speed_zero'
    | 'invalid_runway'
    | null
  /// True for aircraft sitting at the threshold awaiting a takeoff
  /// command. The CommandPanel swaps in a takeoff button instead of
  /// the cleared-to-land control.
  awaitingTakeoff: boolean
  /// Optional context for awaiting-takeoff aircraft.
  departureRunway: { airportName: string; runwayId: string } | null
  /// Optional directTo target name for transition / takeoff aircraft.
  directToName: string | null
  /// Whether the contextual "Cleared for approach" button should be
  /// visible. True when the aircraft is within 15 NM of the active
  /// runway and ≤45° off the localizer (and not already cleared).
  canClearForApproach: boolean
  /// True when the localizer-intercept autopilot is engaged.
  inApproachIntercept: boolean
  /// Active hold pattern, or null. Drives the "Holding CARYN" status
  /// label, the Cancel-hold button, and the per-phase indicator on
  /// the panel.
  hold:
    | {
        fixName: string
        phase: 'flyingToWaypoint' | 'firstTurn' | 'outboundLeg' | 'secondTurn'
      }
    | null
  /// Active emergency (if any) — drives the alert badge on the panel.
  emergency: { reason: EmergencyReason; countdownSec: number } | null
  /// True when this aircraft may be handed off to Center — i.e. it's a
  /// "leaving" aircraft (a transition / pass-through goal), not an arrival
  /// you're meant to land. Gates the Hand off button (live levels only).
  canHandoff: boolean
  /// User-friendly description of the aircraft's goal.
  goalDescription: string
  /// Live collision-detector verdict + a short reason. The panel
  /// renders a banner when this is anything other than 'normal'.
  collisionStatus: 'normal' | 'conflict' | 'terrainWarning' | 'crashed'
  collisionDetail: string | null
  /// Where the aircraft is currently heading — drives the
  /// distance-to-airport HUD beneath the score. Priority order:
  ///   1. `directToPoint` (player direct-to, SID/STAR leg, hold
  ///      homing) — name is the waypoint id.
  ///   2. The aircraft's goal target (airport center for land /
  ///      arrival, waypoint for transition / takeoff, SID's last
  ///      leg for departure).
  ///   3. null when no useful target (e.g., already landed).
  targetName: string | null
  /// Great-circle-equivalent distance in NM from current position
  /// to `targetName`. Null when targetName is null.
  distanceToTargetNM: number | null
  /// Seconds-to-target based on closing speed (vector projection
  /// of velocity onto the target direction). Null when heading
  /// away from the target or when on the ground / stationary.
  etaSec: number | null
  /// 4-digit octal transponder squawk. Stamped to "7700" on
  /// emergency. Cosmetic — surfaces on the data tag and flight
  /// strip rows.
  squawk: string
}

export interface ScoreState {
  total: number
  /// Most recent events, newest first. Used by the HUD's flash list.
  recent: ScoreEvent[]
  /// Aggregate counts for the end-screen summary.
  successCount: number
  failCount: number
  expectedTotal: number
  /// Number of successfully-handled goals required to pass the level
  /// at the active difficulty (`Math.ceil(total × minimumSuccessPercentage)`).
  /// Drives the "need N" line in the score HUD.
  targetCompleted: number
  /// Per-category running totals (landings / transitions /
  /// departures / conflicts / bonuses / violations). Drives the
  /// EndScreen breakdown rows.
  breakdown: ScoreBreakdown
  /// Distinct separation-conflict episodes (pair-entry edges, not
  /// per-frame). Crashes are counted separately. Drives the
  /// EndScreen "Skill rating" and "Infringements" lines.
  separationInfringements: number
}

/// Live wind for the WindIndicator chrome. Direction in degrees
/// magnetic; speed in knots. Phase 1 keeps speed constant, but the
/// shape is ready for the speed sim that lands later.
export interface WindState {
  directionDeg: number
  speedKts: number
}

/// Live-shift goal progress (positioned shifts only). Synced from the session
/// each tick; drives the goal HUD + the EndScreen shift summary. `goal` of 0
/// means there's no active shift goal.
export interface ShiftState {
  aircraftWorked: number
  goal: number
}

/// A pinned arrival check-in (player feedback — dev board, Dandop,
/// 2026-06-11): persists under the wind indicator until the player
/// engages the aircraft (selecting it dismisses the row — and every
/// command requires selection, so the queue self-cleans).
export interface PendingArrival {
  id: string
  flightNumber: string
  detail: string
  isEmergency: boolean
}

const PERSISTENT_ARRIVALS_KEY = 'flatout-persistent-arrivals'
export function persistentArrivalsEnabled(): boolean {
  try {
    return window.localStorage.getItem(PERSISTENT_ARRIVALS_KEY) !== 'off'
  } catch {
    return true
  }
}
export function setPersistentArrivalsEnabled(on: boolean): void {
  try {
    window.localStorage.setItem(PERSISTENT_ARRIVALS_KEY, on ? 'on' : 'off')
  } catch {
    // best-effort; private-mode storage failures are fine
  }
}

interface UiState {
  selectedId: string | null
  selected: AircraftSummary | null
  endState: LevelEndState
  briefVisible: boolean
  flightStripVisible: boolean
  score: ScoreState
  wind: WindState
  shift: ShiftState
  pendingArrivals: PendingArrival[]

  setSelected(id: string | null, summary: AircraftSummary | null): void
  pushPendingArrival(arrival: PendingArrival): void
  dismissPendingArrival(id: string): void
  prunePendingArrivals(liveIds: ReadonlySet<string>): void
  setSelectedSummary(summary: AircraftSummary | null): void
  setEndState(state: LevelEndState): void
  setBriefVisible(v: boolean): void
  setFlightStripVisible(v: boolean): void
  setScore(score: ScoreState): void
  setWind(wind: WindState): void
  setShift(shift: ShiftState): void
}

export const useUiStore = create<UiState>((set) => ({
  selectedId: null,
  selected: null,
  pendingArrivals: [],
  endState: { kind: 'inProgress' },
  briefVisible: false,
  flightStripVisible: false,
  score: {
    total: 0,
    recent: [],
    successCount: 0,
    failCount: 0,
    expectedTotal: 0,
    targetCompleted: 0,
    breakdown: {
      landings: { count: 0, points: 0 },
      transitions: { count: 0, points: 0 },
      departures: { count: 0, points: 0 },
      conflicts: { count: 0, points: 0 },
      bonuses: { count: 0, points: 0 },
      violations: { count: 0, points: 0 },
    },
    separationInfringements: 0,
  },
  wind: { directionDeg: 0, speedKts: 0 },
  shift: { aircraftWorked: 0, goal: 0 },

  setSelected: (id, summary) =>
    set((s) => ({
      selectedId: id,
      selected: summary,
      // Selecting an aircraft is the "I've seen it" signal — its
      // pinned arrival row (if any) dismisses.
      pendingArrivals: id
        ? s.pendingArrivals.filter((p) => p.id !== id)
        : s.pendingArrivals,
    })),
  pushPendingArrival: (arrival) =>
    set((s) =>
      s.pendingArrivals.some((p) => p.id === arrival.id)
        ? {}
        : { pendingArrivals: [...s.pendingArrivals, arrival] },
    ),
  dismissPendingArrival: (id) =>
    set((s) =>
      s.pendingArrivals.some((p) => p.id === id)
        ? { pendingArrivals: s.pendingArrivals.filter((p) => p.id !== id) }
        : {},
    ),
  prunePendingArrivals: (liveIds) =>
    set((s) =>
      s.pendingArrivals.some((p) => !liveIds.has(p.id))
        ? { pendingArrivals: s.pendingArrivals.filter((p) => liveIds.has(p.id)) }
        : {},
    ),
  setSelectedSummary: (summary) => set({ selected: summary }),
  setEndState: (state) => set({ endState: state }),
  setBriefVisible: (v) => set({ briefVisible: v }),
  setFlightStripVisible: (v) => set({ flightStripVisible: v }),
  setScore: (score) => set({ score }),
  setWind: (wind) => set({ wind }),
  setShift: (shift) => set({ shift }),
}))
